import { useCallback, useEffect, useRef, useState } from 'react'
import GameScene from './game3d/GameScene'
import GameOverModal from './GameOverModal'
import FeedbackModal from './FeedbackModal'
import { useGame } from '../hooks/useGame'
import { Position } from '../stores/gameStore'
import { track } from '../utils/analytics'

interface Game3DProps {
  onBack: () => void
}

export default function Game3D({ onBack }: Game3DProps) {
  const game = useGame()
  const didStart = useRef(false)
  const didFirstMove = useRef(false)
  const didComplete = useRef(false)
  const [showFeedback, setShowFeedback] = useState(false)

  useEffect(() => {
    if (didStart.current) return
    didStart.current = true
    void game.startNewGame()
    track('game_started', { difficulty: game.aiDifficulty, mode: 'vs_ai' })
  }, [game])

  // Fires once per finished game — `winner` stays set for as long as the modal is up.
  useEffect(() => {
    if (game.winner === null || didComplete.current) return
    didComplete.current = true
    const won = game.winner === game.playerColor
    track('game_completed', {
      difficulty: game.aiDifficulty,
      result: game.winner === 0 ? 'draw' : won ? 'win' : 'loss',
      moves: game.moveHistory.length,
    })
    if (won) track('player_won', { difficulty: game.aiDifficulty, moves: game.moveHistory.length })
  }, [game.winner, game.playerColor, game.aiDifficulty, game.moveHistory.length])

  const rematch = useCallback(() => {
    didFirstMove.current = false
    didComplete.current = false
    void game.startNewGame()
    track('game_started', { difficulty: game.aiDifficulty, mode: 'vs_ai' })
  }, [game])

  const handlePositionClick = useCallback(
    (pos: Position) => {
      if (game.isAIThinking || game.animatingPiece || game.winner !== null) return

      const piece = game.pieces.find(
        (candidate) =>
          candidate.position.q === pos.q && candidate.position.r === pos.r,
      )

      if (!game.selectedPiece) {
        if (piece && piece.player === game.currentPlayer) game.selectPiece(pos)
        return
      }

      const move = game.validMoves.find(
        (candidate) =>
          candidate.end.q === pos.q && candidate.end.r === pos.r,
      )

      if (move) {
        if (!didFirstMove.current) {
          didFirstMove.current = true
          track('first_move_made', { difficulty: game.aiDifficulty })
        }
        void game.makeMove(move)
        return
      }

      if (piece && piece.player === game.currentPlayer) {
        game.selectPiece(pos)
      } else {
        game.selectPiece(null)
      }
    },
    [game],
  )

  const status = getStatusText({
    winner: game.winner,
    playerColor: game.playerColor,
    currentPlayer: game.currentPlayer,
    isAIThinking: game.isAIThinking,
    isLoading: game.isLoading,
    error: game.error,
  })

  return (
    <main className="game-screen">
      <GameScene
        pieces={game.pieces}
        marbleStyle={game.marbleStyle}
        selectedPiece={game.selectedPiece}
        validMoves={game.validMoves}
        animatingPiece={game.animatingPiece}
        onPositionClick={handlePositionClick}
      />

      <div className="game-overlay">
        <div className={`status-pill ${game.error ? 'status-pill-error' : ''}`}>
          {status}
        </div>
        <button className="wood-button" type="button" onClick={onBack}>
          Back
        </button>
      </div>

      {game.winner !== null && !showFeedback && (
        <GameOverModal
          won={game.winner === game.playerColor}
          draw={game.winner === 0}
          difficulty={game.aiDifficulty}
          moveCount={game.moveHistory.length}
          onRematch={rematch}
          onBack={onBack}
          onFeedback={() => {
            track('feedback_opened', { context: 'post_game' })
            setShowFeedback(true)
          }}
        />
      )}

      {showFeedback && (
        <FeedbackModal context="post_game" onClose={() => setShowFeedback(false)} />
      )}
    </main>
  )
}

function getStatusText({
  winner,
  playerColor,
  currentPlayer,
  isAIThinking,
  isLoading,
  error,
}: {
  winner: number | null
  playerColor: number
  currentPlayer: number
  isAIThinking: boolean
  isLoading: boolean
  error: string | null
}) {
  if (error) return error
  if (winner !== null) return winner === playerColor ? 'You win' : 'Opponent wins'
  if (isLoading && !isAIThinking) return 'Loading...'
  if (isAIThinking) return 'Thinking...'
  return currentPlayer === playerColor ? 'Your turn' : "Opponent's turn"
}
